import React, { useEffect, useMemo, useState } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import {
  Box,
  Checkbox,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Apps as AppsIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import DataTable from "components/common/tables/DataTable";
import { getToken } from "services/auth.service";
import { checkPermission } from "services/permission.service";
import { useAuth } from "context/AuthContext";
import { usePageTitle } from "context/PageTitleContext";
import { PERMISSIONS } from "auth/permissionCodes";
import "./ApplicationManagement.css";
import { getRoleColor, isAdminRole } from "modules/systems/utils/permission.utils";
import { AppTextField } from "components/common/inputs";
import { AppTab, AppTabPanel } from "components/common/tabs";

const API_URL = process.env.REACT_APP_API_URL;

const getRoleOrder = (roleName) => {
  const roleOrder = {
    Admin: 1,
    Operator: 2,
    Creator: 3,
    Editor: 4,
    Viewer: 5,
  };

  return roleOrder[roleName] || 999;
};

const ApplicationManagement = () => {
  const { user } = useAuth();
  const { setPageTitle } = usePageTitle();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [applications, setApplications] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [applicationPermissions, setApplicationPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [searchApplication, setSearchApplication] = useState("");
  const [searchRole, setSearchRole] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const tabs = [
    { label: "Vai trò", icon: <GroupIcon />, value: 0 },
    { label: "Người dùng trực tiếp", icon: <PersonIcon />, value: 1 },
    { label: "Tổng hợp truy cập", icon: <SecurityIcon />, value: 2 },
  ];

  useEffect(() => {
    setPageTitle("Phân quyền ứng dụng");
  }, [setPageTitle]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && user.role === "Admin") {
      setCanManage(true);
    } else if (user) {
      checkPermission(PERMISSIONS.SYSTEM.PERMISSION_APPLICATION_ASSIGN).then((result) => {
        setCanManage(result.hasPermission === true);
      });
    } else {
      setCanManage(false);
    }
  }, [user]);

  useEffect(() => {
    if (!selectedApplicationId && applications.length > 0) {
      setSelectedApplicationId(applications[0].applicationId);
    }
  }, [applications, selectedApplicationId]);

  const selectedApplication = useMemo(
    () =>
      applications.find(
        (application) => Number(application.applicationId) === Number(selectedApplicationId)
      ),
    [applications, selectedApplicationId]
  );

  const getApplicationModuleCode = (application) => {
    const accessCode =
      application?.accessPermissionCode ||
      application?.AccessPermissionCode ||
      application?.applicationCode ||
      application?.ApplicationCode ||
      "";
    const codeParts = String(accessCode).toLowerCase().split(".");

    if (codeParts[0] === "pms" && codeParts.length > 1) {
      return codeParts[1];
    }

    return codeParts[0] || "";
  };

  const getRoleModuleCode = (role) => {
    const roleCode = role?.roleCode || role?.RoleCode || "";
    const codeParts = String(roleCode).toLowerCase().split(".");
    return codeParts[0] === "pms" ? codeParts[1] || "" : codeParts[0] || "";
  };

  const roleMatchesApplication = (role, application) => {
    if (!application) return true;

    const roleApplicationId = role.applicationId ?? role.ApplicationId;
    if (roleApplicationId) {
      return Number(roleApplicationId) === Number(application.applicationId);
    }

    const roleModuleCode = getRoleModuleCode(role);
    if (roleModuleCode) {
      return roleModuleCode === getApplicationModuleCode(application);
    }

    return false;
  };

  const selectedApplicationAccessPermission = useMemo(() => {
    if (!selectedApplication) return null;
    const accessCode =
      selectedApplication.accessPermissionCode ||
      selectedApplication.AccessPermissionCode ||
      selectedApplication.applicationCode ||
      selectedApplication.ApplicationCode;

    return applicationPermissions.find(
      (permission) =>
        (permission.permissionCode || permission.PermissionCode || permission.permissionName) === accessCode
    );
  }, [applicationPermissions, selectedApplication]);

  const selectedAccessPermissionId = selectedApplicationAccessPermission?.permissionId;

  const roleApplicationMap = useMemo(() => {
    const map = {};
    rolePermissions.forEach((item) => {
      if (!map[item.roleId]) {
        map[item.roleId] = [];
      }
      map[item.roleId].push(item.permissionId);
    });
    return map;
  }, [rolePermissions]);

  const selectedRoleApplications = useMemo(
    () =>
      rolePermissions.filter(
        (item) => Number(item.permissionId) === Number(selectedAccessPermissionId)
      ),
    [rolePermissions, selectedAccessPermissionId]
  );

  const selectedUserApplications = useMemo(
    () =>
      userPermissions.filter(
        (item) => Number(item.permissionId) === Number(selectedAccessPermissionId)
      ),
    [userPermissions, selectedAccessPermissionId]
  );

  const selectedRoleIds = useMemo(
    () => new Set(selectedRoleApplications.map((item) => item.roleId)),
    [selectedRoleApplications]
  );

  const selectedDirectUserIds = useMemo(
    () => new Set(selectedUserApplications.map((item) => item.userId)),
    [selectedUserApplications]
  );

  const getRoleDetails = (role) =>
    roles.find(
      (candidate) =>
        Number(candidate.roleId ?? candidate.RoleId) === Number(role.roleId ?? role.RoleId)
    );

  const userRoleMatchesApplication = (userRole) => {
    if (!selectedApplication) return true;

    const roleDetails = getRoleDetails(userRole);
    const roleToCheck = roleDetails ? { ...userRole, ...roleDetails } : userRole;
    const roleApplicationId = roleToCheck.applicationId ?? roleToCheck.ApplicationId;
    if (roleApplicationId) {
      return Number(roleApplicationId) === Number(selectedApplication.applicationId);
    }

    const roleModuleCode = getRoleModuleCode(roleToCheck);
    if (roleModuleCode) {
      return roleModuleCode === getApplicationModuleCode(selectedApplication);
    }

    return false;
  };

  const inheritedUsers = useMemo(() => {
    if (!selectedAccessPermissionId) return [];

    const selectedRoleIdSet = new Set(selectedRoleIds);
    return userRoles
      .filter(
        (item) =>
          userRoleMatchesApplication(item) &&
          (isAdminRole(item) || selectedRoleIdSet.has(item.roleId))
      )
      .map((item) => {
        const matchedUser = users.find((candidate) => candidate.userId === item.userId);
        return {
          ...item,
          userName: item.userName || matchedUser?.userName,
          fullName: item.fullName || matchedUser?.fullName,
          email: matchedUser?.email,
          isActive: matchedUser?.isActive,
        };
      });
  }, [roles, selectedAccessPermissionId, selectedApplication, selectedRoleIds, userRoles, users]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedAdminUserIds = useMemo(
    () => new Set(
      userRoles
        .filter((item) =>
          userRoleMatchesApplication(item) && isAdminRole(getRoleDetails(item) || item)
        )
        .map((item) => Number(item.userId))
    ),
    [roles, selectedApplication, userRoles] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const effectiveUsers = useMemo(() => {
    const map = new Map();

    inheritedUsers.forEach((item) => {
      if (!map.has(item.userId)) {
        map.set(item.userId, {
          userId: item.userId,
          userName: item.userName,
          fullName: item.fullName,
          email: item.email,
          isActive: item.isActive,
          roleNames: [],
          hasDirectAccess: selectedDirectUserIds.has(item.userId),
          hasInheritedAccess: true,
        });
      }

      const current = map.get(item.userId);
      if (item.roleName && !current.roleNames.includes(item.roleName)) {
        current.roleNames.push(item.roleName);
      }
    });

    selectedUserApplications.forEach((item) => {
      const matchedUser = users.find((candidate) => candidate.userId === item.userId);

      if (!map.has(item.userId)) {
        map.set(item.userId, {
          userId: item.userId,
          userName: item.userName || matchedUser?.userName,
          fullName: item.fullName || matchedUser?.fullName,
          email: matchedUser?.email,
          isActive: matchedUser?.isActive,
          roleNames: [],
          hasDirectAccess: true,
          hasInheritedAccess: false,
        });
      } else {
        map.get(item.userId).hasDirectAccess = true;
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      (a.fullName || a.userName || "").localeCompare(b.fullName || b.userName || "")
    );
  }, [inheritedUsers, selectedDirectUserIds, selectedUserApplications, users]);

  const filteredApplications = useMemo(
    () =>
      applications.filter(
        (application) =>
          application.applicationName
            ?.toLowerCase()
            .includes(searchApplication.toLowerCase()) ||
          application.description
            ?.toLowerCase()
            .includes(searchApplication.toLowerCase()) ||
          application.accessPermissionCode
            ?.toLowerCase()
            .includes(searchApplication.toLowerCase())
      ),
    [applications, searchApplication]
  );

  const filteredRoles = useMemo(
    () =>
      roles
        .filter(
          (role) =>
            roleMatchesApplication(role, selectedApplication) &&
            (role.roleName?.toLowerCase().includes(searchRole.toLowerCase()) ||
              (role.roleCode || role.RoleCode)?.toLowerCase().includes(searchRole.toLowerCase()) ||
              role.description?.toLowerCase().includes(searchRole.toLowerCase()))
        )
        .sort((a, b) => getRoleOrder(a.roleName) - getRoleOrder(b.roleName)),
    [roles, searchRole, selectedApplication] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const filteredUsers = useMemo(
    () =>
      users.filter(
        (item) =>
          item.userName?.toLowerCase().includes(searchUser.toLowerCase()) ||
          item.fullName?.toLowerCase().includes(searchUser.toLowerCase()) ||
          item.email?.toLowerCase().includes(searchUser.toLowerCase())
      ),
    [users, searchUser]
  );

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchJson = async (url) => {
    const token = getToken();
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${url}`);
    }

    return response.json();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [
        applicationResponse,
        permissionResponse,
        roleResponse,
        userResponse,
        rolePermissionResponse,
        userPermissionResponse,
        userRoleResponse,
      ] = await Promise.all([
        fetchJson(`${API_URL}/api/Application/all`),
        fetchJson(`${API_URL}/api/SystemPermissions/all`),
        fetchJson(`${API_URL}/api/RolesManagement?pageSize=1000&pageNumber=1`),
        fetchJson(`${API_URL}/api/User?pageSize=1000&pageNumber=1`),
        fetchJson(`${API_URL}/api/RolePermission?pageSize=1000&pageNumber=1`),
        fetchJson(`${API_URL}/api/UserPermission?pageSize=1000&pageNumber=1`),
        fetchJson(`${API_URL}/api/UserRole`),
      ]);

      setApplications(applicationResponse.data || []);
      setApplicationPermissions(
        (permissionResponse.data || []).filter(
          (permission) =>
            (permission.permissionType || permission.PermissionType) === "Application" ||
            (permission.actionCode || permission.ActionCode) === "access"
        )
      );
      setRoles(roleResponse.data || []);
      setUsers(userResponse.data || []);
      setRolePermissions(rolePermissionResponse.data || []);
      setUserPermissions(userPermissionResponse.data || []);
      setUserRoles(userRoleResponse.data || []);
    } catch (error) {
      showSnackbar("Lỗi khi tải dữ liệu phân quyền ứng dụng", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = async (role, checked) => {
    if (!selectedAccessPermissionId || saving) return;

    if (!canManage) {
      showSnackbar("Bạn không có quyền cập nhật phân quyền ứng dụng", "warning");
      return;
    }

    if (isAdminRole(role)) {
      showSnackbar("Không thể chỉnh sửa phân quyền ứng dụng của vai trò Admin", "info");
      return;
    }

    const currentPermissionIds = roleApplicationMap[role.roleId] || [];
    const nextPermissionIds = checked
      ? Array.from(new Set([...currentPermissionIds, selectedAccessPermissionId]))
      : currentPermissionIds.filter(
        (permissionId) => Number(permissionId) !== Number(selectedAccessPermissionId)
      );

    setSaving(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/RolePermission/role/${role.roleId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          roleId: role.roleId,
          permissionIds: nextPermissionIds,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save role application access permission");
      }

      const refreshed = await fetchJson(
        `${API_URL}/api/RolePermission?pageSize=1000&pageNumber=1`
      );
      setRolePermissions(refreshed.data || []);
      showSnackbar("Đã cập nhật vai trò truy cập ứng dụng", "success");
    } catch (error) {
      showSnackbar("Lỗi khi cập nhật vai trò truy cập ứng dụng", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUserToggle = async (selectedUser, checked) => {
    if (!selectedAccessPermissionId || saving) return;

    if (selectedAdminUserIds.has(Number(selectedUser.userId))) return;

    if (!canManage) {
      showSnackbar("Bạn không có quyền cập nhật phân quyền ứng dụng", "warning");
      return;
    }

    const inherited = inheritedUsers.some(
      (item) => Number(item.userId) === Number(selectedUser.userId)
    );
    if (checked && inherited) {
      showSnackbar(
        "Người dùng này đã có quyền truy cập ứng dụng thông qua vai trò, không cần gán trực tiếp.",
        "warning"
      );
      return;
    }

    const existing = selectedUserApplications.find(
      (item) => item.userId === selectedUser.userId
    );

    setSaving(true);
    try {
      const token = getToken();
      let response;

      if (checked) {
        response = await fetch(`${API_URL}/api/UserPermission`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedUser.userId,
            permissionId: selectedAccessPermissionId,
          }),
        });
      } else if (existing) {
        response = await fetch(`${API_URL}/api/UserPermission/${existing.userPermissionId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        setSaving(false);
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to save user application access permission");
      }

      const refreshed = await fetchJson(
        `${API_URL}/api/UserPermission?pageSize=1000&pageNumber=1`
      );
      setUserPermissions(refreshed.data || []);
      showSnackbar("Đã cập nhật người dùng truy cập ứng dụng", "success");
    } catch (error) {
      showSnackbar("Lỗi khi cập nhật người dùng truy cập ứng dụng", "error");
    } finally {
      setSaving(false);
    }
  };

  const roleColumns = useMemo(
    () => [
      {
        id: "access",
        header: "Truy cập",
        size: 90,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <Checkbox
            size="small"
            checked={selectedRoleIds.has(row.original.roleId)}
            disabled={saving || !canManage || isAdminRole(row.original)}
            onChange={(event) => handleRoleToggle(row.original, event.target.checked)}
          />
        ),
      },
      {
        accessorKey: "roleName",
        header: "Vai trò",
        size: 160,
        Cell: ({ row }) => (
          <Chip
            label={row.original.roleName}
            size="small"
            sx={{
              backgroundColor: getRoleColor(row.original.roleName),
              color: getRoleColor(row.original.roleName) ? "#fff" : undefined,
              fontWeight: 600,
            }}
          />
        ),
      },
      { accessorKey: "description", header: "Mô tả", size: 260 },
      {
        id: "source",
        header: "Ghi chú",
        size: 150,
        Cell: ({ row }) =>
          selectedRoleIds.has(row.original.roleId) ? (
            <Chip label="Được cấp" size="small" color="primary" />
          ) : (
            <Chip label="Chưa cấp" size="small" />
          ),
      },
    ],
    [canManage, saving, selectedRoleIds] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const userColumns = useMemo(
    () => [
      {
        id: "access",
        header: "Trực tiếp",
        size: 90,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <Checkbox
            size="small"
            checked={selectedDirectUserIds.has(row.original.userId)}
            disabled={saving || !canManage || selectedAdminUserIds.has(Number(row.original.userId))}
            onChange={(event) => handleUserToggle(row.original, event.target.checked)}
          />
        ),
      },
      { accessorKey: "userName", header: "Tên đăng nhập", size: 140 },
      { accessorKey: "fullName", header: "Tên đầy đủ", size: 180 },
      { accessorKey: "email", header: "Email", size: 200 },
      {
        id: "status",
        header: "Trạng thái",
        size: 120,
        accessorFn: (row) => !!row.isActive,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? "Kích hoạt" : "Chưa kích hoạt"}
            size="small"
            color={cell.getValue() ? "success" : "warning"}
          />
        ),
      },
      {
        id: "source",
        header: "Nguồn quyền",
        size: 150,
        Cell: ({ row }) => {
          const inheritedRoles = inheritedUsers.filter(
            (item) => Number(item.userId) === Number(row.original.userId)
          );
          const inherited = inheritedRoles.length > 0;
          const direct = selectedDirectUserIds.has(row.original.userId);

          if (inheritedRoles.some(isAdminRole)) {
            return <Chip label="Admin" size="small" color="success" />;
          }

          if (direct && inherited) {
            return <Chip label="Trực tiếp + vai trò" size="small" color="primary" />;
          }

          if (direct) {
            return <Chip label="Trực tiếp" size="small" color="success" />;
          }

          if (inherited) {
            return <Chip label="Theo vai trò" size="small" color="primary" />;
          }

          return <Chip label="Chưa cấp" size="small" />;
        },
      },
    ],
    [canManage, inheritedUsers, saving, selectedAdminUserIds, selectedDirectUserIds] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const effectiveUserColumns = useMemo(
    () => [
      { accessorKey: "userName", header: "Tên đăng nhập", size: 140 },
      { accessorKey: "fullName", header: "Tên đầy đủ", size: 180 },
      { accessorKey: "email", header: "Email", size: 200 },
      {
        id: "roles",
        header: "Vai trò cấp quyền",
        size: 220,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {row.original.roleNames.length > 0 ? (
              row.original.roleNames.map((roleName) => (
                <Chip
                  key={roleName}
                  label={roleName}
                  size="small"
                  sx={{
                    backgroundColor: getRoleColor(roleName),
                    color: getRoleColor(roleName) ? "#fff" : undefined,
                    fontWeight: 600,
                  }}
                />
              ))
            ) : (
              <Chip label="Không có" size="small" />
            )}
          </Box>
        ),
      },
      {
        id: "source",
        header: "Nguồn truy cập",
        size: 170,
        Cell: ({ row }) => {
          if (row.original.hasDirectAccess && row.original.hasInheritedAccess) {
            return <Chip label="Trực tiếp + vai trò" size="small" color="primary" />;
          }
          if (row.original.hasDirectAccess) {
            return <Chip label="Trực tiếp" size="small" color="success" />;
          }
          return <Chip label="Theo vai trò" size="small" color="primary" />;
        },
      },
    ],
    []
  );

  return (
    <Box className="application-management">
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} className="application-list-panel">
            <Box className="application-panel-header">
              <AppsIcon />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Ứng dụng
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Chọn ứng dụng để quản lý phạm vi truy cập
                </Typography>
              </Box>
            </Box>

            <AppTextField
              fullWidth
              placeholder="Tìm kiếm ứng dụng ..."
              value={searchApplication}
              onChange={(value) => setSearchApplication(value)}
              sx={{ mb: 2 }}
            />

            <Box className="application-list">
              {filteredApplications.map((application) => {
                const isSelected = application.applicationId === selectedApplicationId;
                const accessCode =
                  application.accessPermissionCode ||
                  application.AccessPermissionCode ||
                  application.applicationCode ||
                  application.ApplicationCode;
                const accessPermission = applicationPermissions.find(
                  (permission) =>
                    (permission.permissionCode || permission.PermissionCode || permission.permissionName) === accessCode
                );
                const accessPermissionId = accessPermission?.permissionId;
                const roleCount = rolePermissions.filter(
                  (item) => Number(item.permissionId) === Number(accessPermissionId)
                ).length;
                const directUserCount = userPermissions.filter(
                  (item) => Number(item.permissionId) === Number(accessPermissionId)
                ).length;

                return (
                  <button
                    type="button"
                    key={application.applicationId}
                    className={`application-list-item ${isSelected ? "selected" : ""}`}
                    onClick={() => setSelectedApplicationId(application.applicationId)}
                  >
                    <Box className="application-list-item-main">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {application.applicationName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {application.description || "Không có mô tả"}
                      </Typography>
                    </Box>
                    <Box className="application-list-item-meta">
                      <Chip label={`${roleCount} vai trò`} size="small" />
                      <Chip label={`${directUserCount} user`} size="small" />
                    </Box>
                  </button>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={0} className="application-detail-panel">
            <Box className="application-detail-header">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedApplication
                    ? selectedApplication.applicationName
                    : "Chọn ứng dụng"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedApplication?.description ||
                    "Quản lý vai trò, người dùng trực tiếp và xem tổng hợp truy cập"}
                </Typography>
              </Box>

              {selectedApplication && (
                <Box className="application-summary-chips">
                  <Chip
                    icon={<GroupIcon />}
                    label={`${selectedRoleApplications.length + 1} vai trò`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<PersonIcon />}
                    label={`${effectiveUsers.length} người dùng`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <AppTab
                value={tabValue}
                onChange={(event, newValue) => setTabValue(newValue)}
                tabs={tabs}
              />
            </Box>

            <Box className="application-tab-content">
              <AppTabPanel value={tabValue} index={0}>
                {tabValue === 0 && (
                  <>
                    <AppTextField
                      fullWidth
                      placeholder="Tìm kiếm vai trò ..."
                      value={searchRole}
                      onChange={(value) => setSearchRole(value)}
                      sx={{ mb: 2 }}
                    />
                    <DataTable
                      columns={roleColumns}
                      data={filteredRoles}
                      initialState={{
                        density: "compact",
                        pagination: { pageIndex: 0, pageSize: 10 },
                      }}
                    />
                  </>
                )}
              </AppTabPanel>

              <AppTabPanel value={tabValue} index={1}>
                {tabValue === 1 && (
                  <>
                    <AppTextField
                      fullWidth
                      placeholder="Tìm kiếm người dùng ..."
                      value={searchUser}
                      onChange={(value) => setSearchUser(value)}
                      sx={{ mb: 2 }}
                    />
                    <DataTable
                      columns={userColumns}
                      data={filteredUsers}
                      initialState={{
                        density: "compact",
                        pagination: { pageIndex: 0, pageSize: 10 },
                      }}
                    />
                  </>
                )}
              </AppTabPanel>

              <AppTabPanel value={tabValue} index={2}>
                {tabValue === 2 && (
                  <DataTable
                    columns={effectiveUserColumns}
                    data={effectiveUsers}
                    initialState={{
                      density: "compact",
                      pagination: { pageIndex: 0, pageSize: 10 },
                    }}
                  />
                )}
              </AppTabPanel>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
        message={snackbar.message}
      />
    </Box>
  );
};

export default ApplicationManagement;
